/**
 * Plan Mode Operations
 * Tools for suggesting and enabling plan mode
 */

import { ToolDefinition, ExecutionContext, ToolResult } from "../registry";
import { getPlanModeManager } from "../../planning";
import kleur from "kleur";

/**
 * Tool for the AI to suggest entering plan mode
 * This allows the agent to proactively suggest planning when appropriate
 */
export const suggestPlanModeTool: ToolDefinition = {
  name: "suggest_plan_mode",
  description: `Suggest entering Plan Mode when the user describes a complex feature, new project, or significant changes they want to make.
Use this tool when:
- User describes wanting to CREATE something new (app, feature, system)
- User describes a COMPLEX task that would benefit from planning
- User asks about IMPLEMENTING something that spans multiple files
- User describes ARCHITECTURAL changes

The tool will ask the user if they want to enter plan mode. If they confirm, plan mode will be enabled automatically.

In plan mode, you can only read/analyze code and create detailed implementation plans - no file modifications until the user exits plan mode.`,
  parameters: {
    type: "object" as const,
    properties: {
      reason: {
        type: "string",
        description: "Brief explanation of why plan mode would be helpful for this task"
      },
      task_summary: {
        type: "string",
        description: "One-line summary of what the user wants to accomplish"
      }
    },
    required: ["reason", "task_summary"]
  },
  execute: async (params: { reason: string; task_summary: string }, context: ExecutionContext): Promise<ToolResult> => {
    const { reason, task_summary } = params;
    const planModeManager = getPlanModeManager(context.workingDirectory, context.sessionId);

    // Check if already in plan mode
    if (planModeManager.isEnabled()) {
      return {
        success: true,
        content: "Already in Plan Mode. Continue with your analysis and planning.",
        metadata: { already_enabled: true }
      };
    }

    // Display suggestion to user
    console.log();
    console.log(kleur.cyan('+-' + '-'.repeat(58) + '-+'));
    console.log(kleur.cyan('|') + kleur.yellow(' Plan Mode Suggested') + ' '.repeat(39) + kleur.cyan('|'));
    console.log(kleur.cyan('|') + ' '.repeat(60) + kleur.cyan('|'));
    console.log(kleur.cyan('|') + kleur.white(` Task: ${task_summary.substring(0, 52).padEnd(52)}`) + kleur.cyan('|'));
    console.log(kleur.cyan('|') + ' '.repeat(60) + kleur.cyan('|'));
    console.log(kleur.cyan('|') + kleur.gray(` ${reason.substring(0, 58).padEnd(58)}`) + kleur.cyan('|'));
    console.log(kleur.cyan('|') + ' '.repeat(60) + kleur.cyan('|'));
    console.log(kleur.cyan('|') + kleur.white(' Plan Mode allows me to:') + ' '.repeat(35) + kleur.cyan('|'));
    console.log(kleur.cyan('|') + kleur.gray('   - Analyze your codebase thoroughly') + ' '.repeat(21) + kleur.cyan('|'));
    console.log(kleur.cyan('|') + kleur.gray('   - Create a detailed implementation plan') + ' '.repeat(16) + kleur.cyan('|'));
    console.log(kleur.cyan('|') + kleur.gray('   - Identify potential issues upfront') + ' '.repeat(20) + kleur.cyan('|'));
    console.log(kleur.cyan('|') + ' '.repeat(60) + kleur.cyan('|'));
    console.log(kleur.cyan('+-' + '-'.repeat(58) + '-+'));
    console.log();

    // Ask for confirmation using the permission manager if available
    if (context.permissionManager) {
      try {
        const approved = await context.permissionManager.requestApproval(
          'enter_plan_mode',
          'Enter Plan Mode for detailed analysis and planning?',
          { reason, task_summary },
          'low'
        );

        if (approved) {
          planModeManager.enable();
          console.log(kleur.blue('\nPlan Mode enabled.'));
          console.log(kleur.gray('I will now analyze and create a detailed plan before making any changes.'));
          console.log();

          return {
            success: true,
            content: `Plan Mode enabled. I will now analyze the codebase and create a detailed implementation plan for: "${task_summary}". I will NOT make any file changes until you exit plan mode with /execute.`,
            metadata: { plan_mode_enabled: true, task: task_summary }
          };
        } else {
          return {
            success: true,
            content: `User declined Plan Mode. I will proceed with implementing "${task_summary}" directly. Let me know if you'd like me to plan first by typing /plan.`,
            metadata: { plan_mode_enabled: false, declined: true }
          };
        }
      } catch (error) {
        // If approval system fails, just inform and continue
        return {
          success: true,
          content: `I suggested Plan Mode for this task. If you'd like me to plan before implementing, type /plan. Otherwise, I'll proceed with the implementation.`,
          metadata: { plan_mode_enabled: false, suggestion_only: true }
        };
      }
    }

    return {
      success: true,
      content: `I recommend using Plan Mode for this task. Type /plan to enter plan mode, or I can proceed with implementation directly.`,
      metadata: { plan_mode_enabled: false, suggestion_only: true }
    };
  }
};

/**
 * Tool to check current plan mode status
 */
export const checkPlanModeTool: ToolDefinition = {
  name: "check_plan_mode",
  description: "Check if plan mode is currently enabled. Use this to verify the current mode before attempting operations.",
  parameters: {
    type: "object" as const,
    properties: {},
    required: []
  },
  execute: async (params: {}, context: ExecutionContext): Promise<ToolResult> => {
    const planModeManager = getPlanModeManager(context.workingDirectory, context.sessionId);
    const isEnabled = planModeManager.isEnabled();
    const currentPlan = planModeManager.getCurrentPlan();

    let content = isEnabled
      ? "Plan Mode is ENABLED. You can only use read-only tools (read_file, grep, list_files, git_status, etc.). Use /execute to exit plan mode and start implementing."
      : "Plan Mode is DISABLED. You can use all tools including write_file, edit_file, bash, etc.";

    if (isEnabled && currentPlan) {
      content += `\n\nCurrent Plan: "${currentPlan.title}" with ${currentPlan.steps.length} steps.`;
    }

    return {
      success: true,
      content,
      metadata: {
        plan_mode_enabled: isEnabled,
        current_plan: currentPlan ? { title: currentPlan.title, steps: currentPlan.steps.length } : null
      }
    };
  }
};
